import React, { useState } from "react";
import { X, Tag, Plus, Loader2 } from "lucide-react";

export default function VendorModal({
  isOpen,
  onClose,
  isEditMode,
  formData,
  setFormData,
  onSubmit,
  error,
  categories,
  paySchedules,
  tagInput,
  setTagInput,
  showTagDropdown,
  setShowTagDropdown,
  availableTags,
  availableBrandTags = [],
  loading
}) {
  const [brandInput, setBrandInput] = useState("");
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: "90%", maxWidth: "850px", maxHeight: "85vh", overflowY: "auto" }}>
        <div className="modal-header">
          <h3>{isEditMode ? "Modify Vendor Details" : "Register New Vendor"}</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={onSubmit}>
          {error && (
            <div className="login-error-alert animate-fade-in" style={{ marginBottom: "1rem" }}>
              {error}
            </div>
          )}
          <div className="vendor-modal-grid">
            {/* Left Column: Basic Details & Notes */}
            <div>
              <h4 className="modal-section-title">General Information</h4>
              {/* Company Name */}
              <div className="form-group">
                <label className="form-label" htmlFor="name">Vendor Company Name *</label>
                <input
                  id="name"
                  type="text"
                  required
                  className="form-input"
                  placeholder="e.g. Atlas Shipping Co."
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              {/* Category & Status */}
              <div className="grid-2" style={{ gap: "1rem", gridTemplateColumns: "1fr 1fr" }}>
                <div className="form-group">
                  <label className="form-label" htmlFor="category">Category *</label>
                  <select
                    id="category"
                    className="form-input"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="status">Status *</label>
                  <select
                    id="status"
                    className="form-input"
                    value={formData.status}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData({
                        ...formData,
                        status: val,
                        blacklist: val === "Blacklist",
                        blacklistReason: val === "Blacklist" ? (formData.blacklistReason || "") : ""
                      });
                    }}
                    required
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Blacklist">Blacklist</option>
                  </select>
                </div>
              </div>

              {/* Pay Schedule */}
              <div className="form-group">
                <label className="form-label" htmlFor="paySchedule">Pay Schedule</label>
                <select
                  id="paySchedule"
                  className="form-input"
                  value={formData.paySchedule || ""}
                  onChange={(e) => setFormData({ ...formData, paySchedule: e.target.value })}
                >
                  <option value="">None Selected</option>
                  {paySchedules.map(ps => (
                    <option key={ps.id} value={ps.name}>{ps.name}</option>
                  ))}
                </select>
              </div>

              {/* Product Tags (Pills with Autocomplete) */}
              <div className="form-group" style={{ position: "relative" }}>
                <label className="form-label" style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                  <Tag size={16} className="text-secondary" />
                  Product Tags
                </label>
                
                {/* Selected Tags Pills Container */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.5rem" }}>
                  {formData.tags && formData.tags.map(tag => (
                    <span key={tag} className="sku-badge" style={{ display: "flex", alignItems: "center", gap: "0.25rem", padding: "0.2rem 0.5rem", background: "var(--primary-hover)", color: "#ffffff", border: "1px solid rgba(255,255,255,0.1)" }}>
                      {tag}
                      <button 
                        type="button" 
                        onClick={() => setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) })}
                        style={{ background: "none", border: "none", color: "#ffffff", cursor: "pointer", fontSize: "0.85rem", padding: 0, display: "flex", alignItems: "center" }}
                        title="Remove Tag"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                  {(!formData.tags || formData.tags.length === 0) && (
                    <span className="text-secondary" style={{ fontSize: "0.8rem", fontStyle: "italic" }}>No tags selected</span>
                  )}
                </div>

                {/* Tag Autocomplete Search Input */}
                <input
                  type="text"
                  className="form-input"
                  placeholder="Type to search and add tags..."
                  value={tagInput}
                  onChange={(e) => {
                    setTagInput(e.target.value);
                    setShowTagDropdown(true);
                  }}
                  onFocus={() => setShowTagDropdown(true)}
                  onBlur={() => setTimeout(() => setShowTagDropdown(false), 200)}
                />

                {/* Suggestions Overlay Dropdown */}
                {showTagDropdown && (
                  <div className="tag-suggestions-dropdown" style={{ 
                    position: "absolute", 
                    top: "100%", 
                    left: 0, 
                    right: 0, 
                    backgroundColor: "var(--bg-card)", 
                    border: "1px solid var(--border-color)", 
                    borderRadius: "4px", 
                    zIndex: 1000, 
                    maxHeight: "150px", 
                    overflowY: "auto", 
                    marginTop: "0.25rem",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.5)"
                  }}>
                    {availableTags
                      .filter(t => !formData.tags.includes(t) && t.toLowerCase().includes(tagInput.toLowerCase()))
                      .sort((a, b) => a.localeCompare(b))
                      .map(tag => (
                        <div
                          key={tag}
                          onMouseDown={() => {
                            setFormData({ ...formData, tags: [...formData.tags, tag] });
                            setTagInput("");
                          }}
                          style={{ 
                            padding: "0.5rem 0.75rem", 
                            cursor: "pointer", 
                            color: "var(--text-primary)"
                          }}
                        >
                          {tag}
                        </div>
                      ))
                    }
                    {availableTags.filter(t => !formData.tags.includes(t) && t.toLowerCase().includes(tagInput.toLowerCase())).length === 0 && (
                      <div style={{ padding: "0.5rem 0.75rem", color: "var(--text-muted)", fontSize: "0.85rem" }}>
                        No matching available tags
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Brand Tags (Pills with Autocomplete) */}
              <div className="form-group" style={{ position: "relative", marginTop: "1rem" }}>
                <label className="form-label" style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                  <Tag size={16} className="text-secondary" />
                  Brand Tags
                </label>
                
                {/* Selected Brand Tags Pills Container */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.5rem" }}>
                  {formData.brandTags && formData.brandTags.map(brand => (
                    <span key={brand} className="sku-badge" style={{ display: "flex", alignItems: "center", gap: "0.25rem", padding: "0.2rem 0.5rem", background: "rgba(16, 185, 129, 0.2)", color: "#10b981", border: "1px solid rgba(16, 185, 129, 0.4)" }}>
                      {brand}
                      <button 
                        type="button" 
                        onClick={() => setFormData({ ...formData, brandTags: formData.brandTags.filter(b => b !== brand) })}
                        style={{ background: "none", border: "none", color: "#10b981", cursor: "pointer", fontSize: "0.85rem", padding: 0, display: "flex", alignItems: "center" }}
                        title="Remove Brand Tag"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                  {(!formData.brandTags || formData.brandTags.length === 0) && (
                    <span className="text-secondary" style={{ fontSize: "0.8rem", fontStyle: "italic" }}>No brand tags selected</span>
                  )}
                </div>

                {/* Brand Tag Autocomplete Search Input */}
                <input
                  type="text"
                  className="form-input"
                  placeholder="Type to search and add brand tags..."
                  value={brandInput}
                  onChange={(e) => {
                    setBrandInput(e.target.value);
                    setShowBrandDropdown(true);
                  }}
                  onFocus={() => setShowBrandDropdown(true)}
                  onBlur={() => setTimeout(() => setShowBrandDropdown(false), 200)}
                />

                {/* Suggestions Overlay Dropdown */}
                {showBrandDropdown && (
                  <div className="tag-suggestions-dropdown" style={{ 
                    position: "absolute", 
                    top: "100%", 
                    left: 0, 
                    right: 0, 
                    backgroundColor: "var(--bg-card)", 
                    border: "1px solid var(--border-color)", 
                    borderRadius: "4px", 
                    zIndex: 1000, 
                    maxHeight: "150px", 
                    overflowY: "auto", 
                    marginTop: "0.25rem",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.5)"
                  }}>
                    {availableBrandTags
                      .filter(b => (!formData.brandTags || !formData.brandTags.includes(b)) && b.toLowerCase().includes(brandInput.toLowerCase()))
                      .sort((a, b) => a.localeCompare(b))
                      .map(brand => (
                        <div
                          key={brand}
                          onMouseDown={() => {
                            setFormData({ ...formData, brandTags: [...(formData.brandTags || []), brand] });
                            setBrandInput("");
                          }}
                          style={{ 
                            padding: "0.5rem 0.75rem", 
                            cursor: "pointer", 
                            color: "var(--text-primary)"
                          }}
                        >
                          {brand}
                        </div>
                      ))
                    }
                    {availableBrandTags.filter(b => (!formData.brandTags || !formData.brandTags.includes(b)) && b.toLowerCase().includes(brandInput.toLowerCase())).length === 0 && (
                      <div style={{ padding: "0.5rem 0.75rem", color: "var(--text-muted)", fontSize: "0.85rem" }}>
                        No matching available brand tags
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="form-label" htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  className="form-input"
                  placeholder="Enter any additional vendor notes or details..."
                  value={formData.notes || ""}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  style={{ minHeight: "80px", resize: "vertical" }}
                />
              </div>
            </div>

            {/* Right Column: Address, Credentials & Status Details */}
            <div>
              <h4 className="modal-section-title">Address & Details</h4>
              {/* Company Address Section */}
              <div className="form-group">
                <label className="form-label" htmlFor="address">Street Address</label>
                <input
                  id="address"
                  type="text"
                  className="form-input"
                  placeholder="e.g. 123 Industrial Parkway"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="address2">Suite / Unit / Building</label>
                <input
                  id="address2"
                  type="text"
                  className="form-input"
                  placeholder="e.g. Suite 400"
                  value={formData.address2}
                  onChange={(e) => setFormData({ ...formData, address2: e.target.value })}
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1.5fr 0.7fr 0.8fr", gap: "0.75rem" }}>
                <div className="form-group">
                  <label className="form-label" htmlFor="city">City</label>
                  <input
                    id="city"
                    type="text"
                    className="form-input"
                    placeholder="e.g. Houston"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="state">State</label>
                  <input
                    id="state"
                    type="text"
                    className="form-input"
                    placeholder="e.g. TX"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="zip">Zip</label>
                  <input
                    id="zip"
                    type="text"
                    className="form-input"
                    placeholder="e.g. 77001"
                    value={formData.zip}
                    onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                  />
                </div>
              </div>

              {/* Additional Vendor Details */}
              <div style={{ marginTop: "1.5rem" }}>
                <div style={{ display: "grid", gridTemplateColumns: formData.taxExempt === "yes" ? "1fr 1fr 1fr" : "1fr 1fr", gap: "0.75rem" }}>
                  <div className="form-group">
                    <label className="form-label" htmlFor="taxExempt">Tax Exempt</label>
                    <select
                      id="taxExempt"
                      className="form-input"
                      value={formData.taxExempt || "no"}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({
                          ...formData,
                          taxExempt: val,
                          taxExemptExpiration: val === "yes" ? (formData.taxExemptExpiration || "") : ""
                        });
                      }}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                  {formData.taxExempt === "yes" && (
                    <div className="form-group">
                      <label className="form-label" htmlFor="taxExemptExpiration">Expiration Date (Optional)</label>
                      <input
                        id="taxExemptExpiration"
                        type="date"
                        className="form-input"
                        value={formData.taxExemptExpiration || ""}
                        onChange={(e) => setFormData({ ...formData, taxExemptExpiration: e.target.value })}
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label" htmlFor="url">Website URL</label>
                    <input
                      id="url"
                      type="text"
                      className="form-input"
                      placeholder="e.g. https://atlas.com"
                      value={formData.url || ""}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginTop: "0.5rem" }}>
                  <div className="form-group">
                    <label className="form-label" htmlFor="accountUser">Account Username</label>
                    <input
                      id="accountUser"
                      type="text"
                      className="form-input"
                      placeholder="e.g. atlas_admin"
                      value={formData.accountUser || ""}
                      onChange={(e) => setFormData({ ...formData, accountUser: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="accountPassword">Account Password</label>
                    <input
                      id="accountPassword"
                      type="text"
                      className="form-input"
                      placeholder="e.g. password123"
                      value={formData.accountPassword || ""}
                      onChange={(e) => setFormData({ ...formData, accountPassword: e.target.value })}
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "0.75rem", marginTop: "0.5rem" }}>
                  <div className="form-group" style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem" }}>
                    <input
                      id="dueDiligenceDone"
                      type="checkbox"
                      checked={formData.dueDiligenceDone || false}
                      onChange={(e) => setFormData({ ...formData, dueDiligenceDone: e.target.checked })}
                      style={{ width: "auto", cursor: "pointer" }}
                    />
                    <label htmlFor="dueDiligenceDone" style={{ fontWeight: "600", cursor: "pointer", color: "var(--text-primary)", margin: 0 }}>
                      Due Diligence Done
                    </label>
                  </div>

                  <div className="form-group" style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem" }}>
                    <input
                      id="blacklist"
                      type="checkbox"
                      checked={formData.blacklist || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData({
                          ...formData,
                          blacklist: checked,
                          status: checked ? "Blacklist" : "Active",
                          blacklistReason: checked ? (formData.blacklistReason || "") : ""
                        });
                      }}
                      style={{ width: "auto", cursor: "pointer" }}
                    />
                    <label htmlFor="blacklist" style={{ fontWeight: "600", cursor: "pointer", color: "var(--danger)", margin: 0 }}>
                      Blacklist Vendor
                    </label>
                  </div>
                </div>

                {formData.blacklist && (
                  <div className="form-group" style={{ marginTop: "0.5rem" }}>
                    <label className="form-label" htmlFor="blacklistReason" style={{ color: "var(--danger)" }}>Reason for Blacklist *</label>
                    <textarea
                      id="blacklistReason"
                      required
                      className="form-input"
                      placeholder="Specify the reason for blacklisting this vendor..."
                      value={formData.blacklistReason || ""}
                      onChange={(e) => setFormData({ ...formData, blacklistReason: e.target.value })}
                      style={{ minHeight: "60px", resize: "vertical" }}
                    />
                  </div>
                )}

                <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "0.75rem" }}>
                  {isEditMode && formData.dateLastVerified && (
                    <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                      <strong>Date Last Verified:</strong> {formData.dateLastVerified}
                    </div>
                  )}
                  {formData.lastPurchasedDate && (
                    <div style={{ fontSize: "0.85rem", color: "var(--primary)" }}>
                      <strong>Last Purchased Date:</strong> {formData.lastPurchasedDate}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Points of Contact List */}
          <div style={{ marginTop: "1.25rem", borderTop: "1px solid var(--border-color)", paddingTop: "1rem", marginBottom: "1rem" }}>
            <h4 style={{ margin: "0 0 0.75rem 0", fontSize: "0.95rem", color: "var(--primary)" }}>Points of Contact</h4>
            {formData.pocs && formData.pocs.map((poc, index) => (
              <div key={index} style={{ border: "1px solid var(--border-color)", padding: "1rem", borderRadius: "6px", marginBottom: "1rem", backgroundColor: "rgba(255, 255, 255, 0.01)", position: "relative" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                  <span style={{ fontSize: "0.9rem", fontWeight: "600", color: "var(--primary)" }}>Contact #{index + 1}</span>
                  {formData.pocs.length > 1 && (
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={() => {
                        const updatedPocs = formData.pocs.filter((_, i) => i !== index);
                        setFormData({ ...formData, pocs: updatedPocs });
                      }}
                      style={{ padding: "0.15rem 0.5rem", minHeight: "auto", fontSize: "0.75rem", color: "var(--danger)", borderColor: "var(--danger)" }}
                    >
                      Remove POC
                    </button>
                  )}
                </div>
                
                <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: "0.75rem" }}>
                  <div className="form-group">
                    <label className="form-label" htmlFor={`poc-name-${index}`}>Contact Name</label>
                    <input
                      id={`poc-name-${index}`}
                      type="text"
                      className="form-input"
                      placeholder="e.g. John Doe"
                      value={poc.name}
                      onChange={(e) => {
                        const updatedPocs = [...formData.pocs];
                        updatedPocs[index] = { ...updatedPocs[index], name: e.target.value };
                        setFormData({ ...formData, pocs: updatedPocs });
                      }}
                      style={{ margin: 0 }}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor={`poc-title-${index}`}>POC Title</label>
                    <input
                      id={`poc-title-${index}`}
                      type="text"
                      className="form-input"
                      placeholder="e.g. Sales Manager"
                      value={poc.title}
                      onChange={(e) => {
                        const updatedPocs = [...formData.pocs];
                        updatedPocs[index] = { ...updatedPocs[index], title: e.target.value };
                        setFormData({ ...formData, pocs: updatedPocs });
                      }}
                      style={{ margin: 0 }}
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: "0.75rem", marginTop: "0.5rem" }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor={`poc-email-${index}`}>Email Address</label>
                    <input
                      id={`poc-email-${index}`}
                      type="email"
                      className="form-input"
                      placeholder="e.g. john@vendor.com"
                      value={poc.email}
                      onChange={(e) => {
                        const updatedPocs = [...formData.pocs];
                        updatedPocs[index] = { ...updatedPocs[index], email: e.target.value };
                        setFormData({ ...formData, pocs: updatedPocs });
                      }}
                      style={{ margin: 0 }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor={`poc-phone-${index}`}>Phone Number</label>
                    <input
                      id={`poc-phone-${index}`}
                      type="text"
                      className="form-input"
                      placeholder="e.g. 555-0100"
                      value={poc.phone}
                      onChange={(e) => {
                        const updatedPocs = [...formData.pocs];
                        updatedPocs[index] = { ...updatedPocs[index], phone: e.target.value };
                        setFormData({ ...formData, pocs: updatedPocs });
                      }}
                      style={{ margin: 0 }}
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setFormData({
                  ...formData,
                  pocs: [...formData.pocs, { name: "", title: "", email: "", phone: "" }]
                });
              }}
              style={{ width: "100%", borderStyle: "dashed", display: "flex", justifyContent: "center", alignItems: "center", gap: "0.5rem", minHeight: "38px" }}
            >
              <Plus size={16} />
              Add Point of Contact
            </button>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1.5rem" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <Loader2 className="spinner-icon" size={16} /> : null}
              {isEditMode ? "Save Changes" : "Register"}
            </button>
          </div>
        </form>
      </div>
      <style>{`
        .vendor-modal-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }

        .modal-section-title {
          margin: 0 0 1rem 0;
          font-size: 0.9rem;
          color: var(--primary);
          border-bottom: 1px solid var(--border-color);
          padding-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: 700;
        }

        @media (min-width: 769px) {
          .vendor-modal-grid {
            grid-template-columns: 1fr 1fr;
          }
        }
      `}</style>
    </div>
  );
}
